import { useState, useMemo, useEffect } from "react";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

// ── Auth config ────────────────────────────────────────────────────────────
// Login form still takes a plain "username" — we map it to a fake email
// internally because Firebase Auth needs an email format. Create these exact
// users (Authentication → Users → Add user) in the Firebase console, and add
// their role here so the app knows who's who.
const ROLE_MAP = {
  khan4pihouse: "مالک",
  muneeb: "منیجر",
};
const usernameToEmail = (username) => `${username.trim()}@espakshop.local`;

// ── Firestore helpers ─────────────────────────────────────────────────────
// Subscribes to a collection in real time — any change any logged-in user
// makes, on any device, appears for everyone instantly.
const listenToCollection = (name, setter) =>
  onSnapshot(collection(db, name), (snap) => {
    setter(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });

const todayStr = () => new Date().toISOString().slice(0, 10);

// Every delete on any page first gets copied here, then auto-purged after
// 30 days — see useLedger's remove() and the DeletedHistoryPage below.
const DELETED_HISTORY_COLLECTION = "deleted_history";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  "Rs " + Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const TABS = [
  "ڈیش بورڈ",
  "چارپائی فروخت",
  "بنتی مزدوری",
  "ولڈنگ سامان",
  "سوتر",
  "کھاتہ پینٹ اینڈ آئل",
  "ڈرم نالی",
  "رپورٹ",
  "حذف شدہ ریکارڈ",
];

// ── Page definitions ──────────────────────────────────────────────────────────
// Every ledger page is described here: its Firestore collection name, its
// fields (in the exact order requested), and whether it should show a
// computed "کل رقم" (amount) column. Add a new page by adding one entry here.
const PAGE_DEFS = [
  {
    key: "charpaiSales",
    collection: "charpai_sales",
    title: "چارپائی فروخت",
    sub: "Charpai Sales",
    color: "var(--green)",
    kind: "income",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "qty", label: "مقدار", type: "number" },
      { key: "size", label: "سائز", type: "text" },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
  },
  {
    key: "binaiMazdoori",
    collection: "binai_mazdoori",
    title: "بنتی مزدوری",
    sub: "Binai Labor",
    color: "var(--gold)",
    kind: "expense",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "qty", label: "مقدار", type: "number" },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
  },
  {
    key: "weldingSaman",
    collection: "welding_saman",
    title: "ولڈنگ سامان",
    sub: "Welding Material",
    color: "var(--blue)",
    kind: "expense",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
  },
  {
    key: "sutar",
    collection: "sutar",
    title: "سوتر",
    sub: "Sutar",
    color: "#7b1fa2",
    kind: "expense",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "qty", label: "مقدار", type: "number" },
      {
        key: "unit",
        label: "یونٹ",
        type: "select",
        options: [
          { value: "count", label: "عدد (Number)" },
          { value: "kg", label: "کلوگرام (KG)" },
        ],
      },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
    // عدد (count) mode: قیمت already is the total — مقدار is just a note, no multiply.
    // کلوگرام (kg) mode: total = مقدار (kg) × قیمت (rate per kg).
    computeAmount: (row) => {
      const qty = +row.qty || 0;
      const price = +row.price || 0;
      return row.unit === "kg" ? qty * price : price;
    },
  },
  {
    key: "khataPaintOil",
    collection: "khata_paint_oil",
    title: "کھاتہ پینٹ اینڈ آئل",
    sub: "Khata — Paint & Oil",
    color: "var(--accent)",
    kind: "ledger",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "number", label: "نمبر", type: "text" },
      { key: "qty", label: "مقدار", type: "number" },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
  },
  {
    key: "drumNali",
    collection: "drum_nali",
    title: "ڈرم نالی",
    sub: "Drum Nali",
    color: "#6b4226",
    kind: "expense",
    fields: [
      { key: "date", label: "تاریخ", type: "date" },
      { key: "name", label: "نام", type: "text" },
      { key: "qty", label: "مقدار", type: "number" },
      { key: "price", label: "قیمت", type: "number", money: true },
    ],
  },
];

// Amount for one row is just the قیمت field itself. مقدار (qty) is a plain
// informational count and is never multiplied in. Pages with no price field
// at all (pure ledgers like Khata Paint & Oil) return null — no amount column.
const rowAmount = (def, row) => {
  const hasPrice = def.fields.some((f) => f.key === "price");
  if (!hasPrice) return null;
  if (def.computeAmount) return def.computeAmount(row);
  return +row.price || 0;
};

const emptyFormFor = (def) => {
  const f = {};
  def.fields.forEach((fld) => {
    if (fld.type === "date") f[fld.key] = todayStr();
    else if (fld.type === "select") f[fld.key] = fld.options?.[0]?.value ?? "";
    else f[fld.key] = "";
  });
  return f;
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+Bhaijaan+2:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #f5f0e8; --surface: #fffdf8; --border: #d4c9b0;
    --ink: #1a1408; --muted: #7a6e58; --accent: #c94a1e;
    --green: #2e7d4f; --blue: #1a5276; --gold: #b8860b;
    --shadow: rgba(26,20,8,0.12);
  }
  body { background: var(--bg); font-family: 'Baloo Bhaijaan 2', sans-serif; direction: rtl; }
  .app { min-height: 100vh; }

  /* LOGIN */
  .login-bg {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: var(--ink);
    background-image: linear-gradient(rgba(201,74,30,0.08) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(201,74,30,0.08) 1px, transparent 1px);
    background-size: 40px 40px;
  }
  .login-card {
    background: var(--surface); border: 1.5px solid var(--border);
    border-radius: 20px; padding: 44px 40px; width: 100%; max-width: 400px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.4);
    animation: loginPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  @keyframes loginPop { from { opacity:0; transform: scale(0.88) translateY(20px); } to { opacity:1; transform: none; } }
  .login-logo { text-align: center; margin-bottom: 28px; }
  .login-logo-icon {
    width: 72px; height: 72px; background: var(--ink); border-radius: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 34px; margin-bottom: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  }
  .login-title { font-size: 24px; font-weight: 800; color: var(--ink); }
  .login-sub { font-size: 12px; color: var(--muted); margin-top: 4px; font-family: 'IBM Plex Mono', monospace; }
  .login-field { margin-bottom: 16px; }
  .login-field label { display: block; font-size: 13px; font-weight: 700; color: var(--muted); margin-bottom: 6px; }
  .login-field input {
    width: 100%; padding: 12px 16px; border: 1.5px solid var(--border); border-radius: 10px;
    font-size: 15px; background: var(--bg); color: var(--ink);
    font-family: 'Baloo Bhaijaan 2', sans-serif; direction: rtl; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .login-field input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(201,74,30,0.12); }
  .login-btn {
    width: 100%; padding: 13px; background: var(--accent); color: white;
    border: none; border-radius: 10px; font-size: 16px; font-weight: 800;
    cursor: pointer; font-family: 'Baloo Bhaijaan 2', sans-serif;
    transition: opacity 0.15s, transform 0.1s; margin-top: 8px;
  }
  .login-btn:hover { opacity: 0.88; transform: translateY(-1px); }
  .login-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .login-error {
    background: #fde8e0; border: 1.5px solid #f4c3b0; border-radius: 8px;
    padding: 10px 14px; color: var(--accent); font-size: 13px; font-weight: 600;
    margin-bottom: 16px; text-align: center;
  }
  .login-hint {
    margin-top: 20px; padding-top: 16px; border-top: 1px dashed var(--border);
    font-size: 11px; color: var(--muted); text-align: center;
    font-family: 'IBM Plex Mono', monospace; line-height: 1.8;
  }

  /* HEADER */
  .header {
    background: var(--ink); color: #fffdf8; padding: 0 24px;
    display: flex; align-items: center; justify-content: space-between;
    height: 64px; position: sticky; top: 0; z-index: 100;
    box-shadow: 0 2px 16px var(--shadow);
  }
  .header-brand { display: flex; align-items: center; gap: 10px; }
  .header-icon { width: 36px; height: 36px; background: var(--accent); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
  .header-title { font-size: 20px; font-weight: 800; letter-spacing: -0.02em; }
  .header-sub { font-size: 11px; color: #a09880; font-family: 'IBM Plex Mono', monospace; }
  .header-right { display: flex; align-items: center; gap: 14px; }
  .header-user { font-size: 12px; color: #a09880; display: flex; align-items: center; gap: 6px; }
  .user-badge { background: rgba(201,74,30,0.25); color: #f4a580; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
  .logout-btn {
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    color: #ccc; padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: 'Baloo Bhaijaan 2', sans-serif; transition: background 0.15s;
  }
  .logout-btn:hover { background: rgba(201,74,30,0.3); color: white; }

  /* NAV */
  .nav { background: var(--surface); border-bottom: 2px solid var(--border); padding: 0 24px; display: flex; overflow-x: auto; }
  .nav-tab { padding: 14px 20px; font-size: 15px; font-weight: 600; color: var(--muted); cursor: pointer; border: none; background: none; border-bottom: 3px solid transparent; margin-bottom: -2px; white-space: nowrap; transition: color 0.15s, border-color 0.15s; font-family: 'Baloo Bhaijaan 2', sans-serif; }
  .nav-tab.active { color: var(--accent); border-bottom-color: var(--accent); }
  .nav-tab:hover { color: var(--ink); }

  /* MAIN */
  .main { padding: 28px 24px; max-width: 1100px; margin: 0 auto; }

  /* TOAST */
  .toast {
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    background: var(--green); color: white; padding: 10px 24px; border-radius: 99px;
    font-size: 14px; font-weight: 700; box-shadow: 0 4px 20px rgba(0,0,0,0.25);
    z-index: 999; pointer-events: none;
    animation: toastIn 0.3s ease both;
  }
  @keyframes toastIn { from { opacity:0; transform: translateX(-50%) translateY(12px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }

  /* KPI */
  .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 32px; }
  .kpi { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; padding: 20px 22px; box-shadow: 0 2px 8px var(--shadow); position: relative; overflow: hidden; }
  .kpi::before { content: ''; position: absolute; top: 0; right: 0; width: 4px; height: 100%; background: var(--kpi-color, var(--accent)); border-radius: 0 14px 14px 0; }
  .kpi-label { font-size: 12px; color: var(--muted); font-weight: 600; letter-spacing: 0.05em; margin-bottom: 6px; }
  .kpi-value { font-size: 26px; font-weight: 800; color: var(--kpi-color, var(--ink)); line-height: 1; }
  .kpi-sub { font-size: 11px; color: var(--muted); margin-top: 4px; font-family: 'IBM Plex Mono', monospace; }

  /* SECTION */
  .section { margin-bottom: 32px; }
  .section-title { font-size: 18px; font-weight: 800; color: var(--ink); margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
  .section-title span { font-size: 11px; font-weight: 500; color: var(--muted); font-family: 'IBM Plex Mono', monospace; background: var(--border); padding: 2px 8px; border-radius: 20px; }

  /* TABLE */
  .table-wrap { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; overflow: hidden; box-shadow: 0 2px 8px var(--shadow); margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: var(--ink); color: #fffdf8; padding: 12px 16px; font-size: 13px; font-weight: 600; text-align: right; }
  td { padding: 11px 16px; font-size: 14px; border-bottom: 1px solid var(--border); }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #faf7f0; }
  .td-num { font-family: 'IBM Plex Mono', monospace; font-size: 13px; }
  .pill { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
  .pill-income  { background: #e8f5e9; color: var(--green); }
  .pill-expense { background: #fde8e0; color: var(--accent); }

  /* FORM */
  .form-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; padding: 24px; box-shadow: 0 2px 8px var(--shadow); margin-bottom: 24px; }
  .form-title { font-size: 16px; font-weight: 800; color: var(--ink); margin-bottom: 18px; }
  .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
  .field { display: flex; flex-direction: column; gap: 5px; }
  .field label { font-size: 12px; font-weight: 600; color: var(--muted); }
  .field input, .field select { border: 1.5px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 14px; background: var(--bg); color: var(--ink); font-family: 'Baloo Bhaijaan 2', sans-serif; direction: rtl; outline: none; transition: border-color 0.15s; }
  .field input:focus, .field select:focus { border-color: var(--accent); }
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 20px; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; border: none; font-family: 'Baloo Bhaijaan 2', sans-serif; transition: opacity 0.15s, transform 0.1s; }
  .btn:hover { opacity: 0.88; transform: translateY(-1px); }
  .btn:active { transform: translateY(0); }
  .btn-primary { background: var(--accent); color: white; }
  .btn-green   { background: var(--green);  color: white; }
  .btn-muted   { background: var(--border); color: var(--ink); }
  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-edit { background: #dbeafe; color: var(--blue); }
  .btn-danger { background: #fee2e2; color: #b91c1c; }
  .form-actions { margin-top: 16px; display: flex; gap: 10px; }
  .row-actions { display: flex; gap: 6px; white-space: nowrap; }

  /* REPORT */
  .report-block { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; padding: 22px; box-shadow: 0 2px 8px var(--shadow); }
  .report-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px dashed var(--border); font-size: 15px; }
  .report-row:last-child { border-bottom: none; }
  .report-row.total { font-weight: 800; font-size: 17px; margin-top: 8px; padding-top: 14px; border-top: 2px solid var(--ink); border-bottom: none; }
  .report-row.profit { color: var(--green); }
  .report-row.loss   { color: var(--accent); }

  /* BAR CHART */
  .bar-chart { display: flex; flex-direction: column; gap: 10px; }
  .bar-row { display: flex; align-items: center; gap: 12px; }
  .bar-label { font-size: 13px; min-width: 140px; text-align: right; color: var(--muted); }
  .bar-track { flex: 1; background: var(--border); border-radius: 99px; height: 18px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 99px; display: flex; align-items: center; padding-right: 8px; justify-content: flex-end; transition: width 0.6s ease; }
  .bar-fill span { font-size: 11px; font-weight: 700; color: white; font-family: 'IBM Plex Mono', monospace; }
  .empty { text-align: center; padding: 40px; color: var(--muted); font-size: 14px; }

  /* CONFIRM MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(26,20,8,0.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 16px;
  }
  .modal-card {
    background: var(--surface); border: 1.5px solid var(--border);
    border-radius: 16px; padding: 28px; width: 100%; max-width: 360px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.35); text-align: center;
    animation: loginPop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  .modal-icon { font-size: 32px; margin-bottom: 12px; }
  .modal-title { font-size: 17px; font-weight: 800; color: var(--ink); margin-bottom: 8px; }
  .modal-sub { font-size: 13px; color: var(--muted); margin-bottom: 22px; line-height: 1.6; }
  .modal-actions { display: flex; gap: 10px; }
  .modal-actions .btn { flex: 1; justify-content: center; }

  /* CONFIRM MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(26,20,8,0.55);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 16px;
  }
  .modal-card {
    background: var(--surface); border: 1.5px solid var(--border);
    border-radius: 16px; padding: 28px; width: 100%; max-width: 360px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.35); text-align: center;
    animation: loginPop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  .modal-icon { font-size: 34px; margin-bottom: 10px; }
  .modal-title { font-size: 17px; font-weight: 800; color: var(--ink); margin-bottom: 6px; }
  .modal-sub { font-size: 13px; color: var(--muted); margin-bottom: 22px; line-height: 1.6; }
  .modal-actions { display: flex; gap: 10px; }
  .modal-actions .btn { flex: 1; justify-content: center; }

  @media(max-width: 600px) {
    .main { padding: 16px 12px; }
    .kpi-value { font-size: 20px; }
    .header { padding: 0 12px; }
    .login-card { margin: 16px; padding: 32px 24px; }
    .header-right { gap: 8px; }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!username || !password) {
      setError("⚠️ یوزرنیم اور پاسورڈ درج کریں");
      return;
    }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(
        auth,
        usernameToEmail(username),
        password,
      );
      // onAuthStateChanged (in the root component) picks up the logged-in
      // user automatically — nothing else to do here.
    } catch (err) {
      setError("❌ غلط یوزرنیم یا پاسورڈ");
      setLoading(false);
    }
  };

  return (
    <div className="login-bg">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">🏭</div>
          <div className="login-title">KHAN 4PI HOUSE شاپ مینیجر</div>
          <div className="login-sub">Environmental Services Pakistan</div>
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="login-field">
          <label>یوزرنیم</label>
          <input
            type="text"
            placeholder="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoComplete="username"
          />
        </div>
        <div className="login-field">
          <label>پاسورڈ</label>
          <input
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoComplete="current-password"
          />
        </div>
        <button className="login-btn" onClick={handleLogin} disabled={loading}>
          {loading ? "⏳ چیک ہو رہا ہے..." : "🔓 لاگ ان کریں"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GENERIC LEDGER HOOK
// One of these per page — subscribes to its own Firestore collection and
// exposes add / edit / update / delete so every page behaves the same way.
// ═══════════════════════════════════════════════════════════════════════════════
function useLedger(def) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyFormFor(def));
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const unsub = listenToCollection(def.collection, (data) => {
      setRows(data);
      setLoading(false);
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPayload = () => {
    const payload = {};
    def.fields.forEach((f) => {
      const v = form[f.key];
      payload[f.key] = f.type === "number" ? +v || 0 : v || "";
    });
    return payload;
  };

  const isValid = () =>
    def.fields.every((f) => {
      const v = form[f.key];
      return v !== "" && v !== undefined && v !== null;
    });

  const resetForm = () => {
    setForm(emptyFormFor(def));
    setEditingId(null);
  };

  const add = async () => {
    if (!isValid()) return false;
    await addDoc(collection(db, def.collection), buildPayload());
    resetForm();
    return true;
  };

  const startEdit = (row) => {
    const f = {};
    def.fields.forEach((fld) => {
      f[fld.key] = row[fld.key] ?? (fld.type === "date" ? todayStr() : "");
    });
    setForm(f);
    setEditingId(row.id);
  };

  const update = async () => {
    if (!editingId || !isValid()) return false;
    await updateDoc(doc(db, def.collection, editingId), buildPayload());
    resetForm();
    return true;
  };

  const remove = async (id) => {
    const row = rows.find((r) => r.id === id);
    if (row) {
      const { id: _drop, ...data } = row;
      try {
        await addDoc(collection(db, DELETED_HISTORY_COLLECTION), {
          pageKey: def.key,
          pageTitle: def.title,
          pageSub: def.sub,
          originalCollection: def.collection,
          fields: def.fields.map((f) => ({
            key: f.key,
            label: f.label,
            type: f.type,
            money: !!f.money,
          })),
          data,
          deletedAt: Date.now(),
        });
      } catch (e) {
        console.error("Failed to log deleted history", e);
      }
    }
    await deleteDoc(doc(db, def.collection, id));
    if (editingId === id) resetForm();
  };

  return {
    rows,
    loading,
    form,
    setForm,
    editingId,
    add,
    startEdit,
    update,
    remove,
    cancelEdit: resetForm,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// GENERIC LEDGER PAGE (form + table with edit/delete) — one per tab
// ═══════════════════════════════════════════════════════════════════════════════
function LedgerPage({ def, ledger, showToast }) {
  const {
    rows,
    form,
    setForm,
    editingId,
    add,
    startEdit,
    update,
    remove,
    cancelEdit,
  } = ledger;

  const hasAmount = def.fields.some((f) => f.key === "price");

  // Holds the id of the row awaiting delete confirmation (null = modal closed)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleSubmit = async () => {
    const ok = editingId ? await update() : await add();
    if (ok) showToast(editingId ? "✅ اپ ڈیٹ ہو گیا" : "💾 محفوظ ہو گیا");
  };

  const askDelete = (id) => setConfirmDeleteId(id);
  const cancelDelete = () => setConfirmDeleteId(null);
  const confirmDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    await remove(id);
    showToast("🗑️ حذف ہو گیا");
  };

  const totalAmount = useMemo(
    () =>
      hasAmount ? rows.reduce((s, r) => s + (rowAmount(def, r) || 0), 0) : 0,
    [rows], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Optional: for the Khata (ledger, no price) page it's useful to see how
  // much quantity is outstanding per person/number.
  const balances = useMemo(() => {
    if (def.kind !== "ledger") return null;
    const map = {};
    rows.forEach((r) => {
      const key = `${r.name || ""} — ${r.number || ""}`;
      map[key] = (map[key] || 0) + (+r.qty || 0);
    });
    return Object.entries(map).map(([label, qty]) => ({ label, qty }));
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="section">
        <div className="section-title">
          {def.title} <span>{def.sub}</span>
        </div>

        <div className="form-card">
          <div className="form-title">
            {editingId ? "✏️ اندراج میں ترمیم کریں" : "➕ نیا اندراج"}
          </div>
          <div className="form-grid">
            {def.fields.map((f) => (
              <div className="field" key={f.key}>
                <label>{f.label}</label>
                {f.type === "select" ? (
                  <select
                    value={form[f.key] ?? ""}
                    onChange={(e) =>
                      setForm((old) => ({ ...old, [f.key]: e.target.value }))
                    }
                  >
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={
                      f.type === "number"
                        ? "number"
                        : f.type === "date"
                          ? "date"
                          : "text"
                    }
                    value={form[f.key] ?? ""}
                    onChange={(e) =>
                      setForm((old) => ({ ...old, [f.key]: e.target.value }))
                    }
                    placeholder={f.type === "number" ? "0" : ""}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSubmit}>
              {editingId ? "اپ ڈیٹ کریں ✓" : "شامل کریں ✓"}
            </button>
            {editingId && (
              <button className="btn btn-muted" onClick={cancelEdit}>
                منسوخ کریں ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi" style={{ "--kpi-color": def.color }}>
          <div className="kpi-label">کل اندراجات</div>
          <div className="kpi-value">{rows.length}</div>
          <div className="kpi-sub">Total Entries</div>
        </div>
        {hasAmount && (
          <div className="kpi" style={{ "--kpi-color": def.color }}>
            <div className="kpi-label">کل رقم</div>
            <div className="kpi-value">{fmt(totalAmount)}</div>
            <div className="kpi-sub">Total Amount</div>
          </div>
        )}
      </div>

      {balances && (
        <div className="section">
          <div className="section-title">
            بلحاظ نام / نمبر خلاصہ <span>Summary by Name / Number</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>نام — نمبر</th>
                  <th>کل مقدار</th>
                </tr>
              </thead>
              <tbody>
                {balances.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="empty">
                      کوئی ڈیٹا موجود نہیں
                    </td>
                  </tr>
                ) : (
                  balances.map((b) => (
                    <tr key={b.label}>
                      <td>{b.label}</td>
                      <td className="td-num">{b.qty}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-title">
          تمام اندراجات <span>All Records</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {def.fields.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
                {hasAmount && <th>کل رقم</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={def.fields.length + (hasAmount ? 2 : 1)}
                    className="empty"
                  >
                    کوئی ڈیٹا موجود نہیں
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    {def.fields.map((f) => (
                      <td
                        key={f.key}
                        className={f.type === "number" ? "td-num" : undefined}
                      >
                        {f.type === "select"
                          ? f.options.find((o) => o.value === r[f.key])
                              ?.label || r[f.key]
                          : f.money
                            ? fmt(r[f.key])
                            : r[f.key]}
                      </td>
                    ))}
                    {hasAmount && (
                      <td className="td-num">{fmt(rowAmount(def, r))}</td>
                    )}
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn btn-sm btn-edit"
                          onClick={() => startEdit(r)}
                        >
                          ترمیم
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => askDelete(r.id)}
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmDeleteId && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">🗑️</div>
            <div className="modal-title">کیا آپ واقعی حذف کرنا چاہتے ہیں؟</div>
            <div className="modal-sub">
              یہ اندراج مستقل طور پر حذف ہو جائے گا۔ اس عمل کو واپس نہیں کیا جا
              سکتا۔
            </div>
            <div className="modal-actions">
              <button className="btn btn-muted" onClick={cancelDelete}>
                منسوخ کریں
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                حذف کریں 🗑️
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DELETED HISTORY — every delete on every page lands here for 30 days, then
// is purged automatically. This hook stays mounted for the whole Dashboard
// session, so purging happens on any snapshot update, not just when the
// person visits the "حذف شدہ ریکارڈ" tab.
// ═══════════════════════════════════════════════════════════════════════════════
function useDeletedHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, DELETED_HISTORY_COLLECTION),
      async (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
        setLoading(false);

        const cutoff = Date.now() - THIRTY_DAYS_MS;
        const expired = data.filter((d) => (d.deletedAt || 0) < cutoff);
        for (const item of expired) {
          try {
            await deleteDoc(doc(db, DELETED_HISTORY_COLLECTION, item.id));
          } catch (e) {
            console.error("Failed to purge expired deleted-history entry", e);
          }
        }
      },
    );
    return unsub;
  }, []);

  return { rows, loading };
}

function DeletedHistoryPage({ rows, loading }) {
  const daysLeft = (deletedAt) => {
    const elapsed = Date.now() - (deletedAt || 0);
    return Math.max(0, 30 - Math.floor(elapsed / (24 * 60 * 60 * 1000)));
  };

  const summarize = (row) =>
    (row.fields || [])
      .map((f) => {
        const raw = row.data ? row.data[f.key] : undefined;
        const val = f.money ? fmt(raw) : raw;
        return `${f.label}: ${val === "" || val == null ? "—" : val}`;
      })
      .join("، ");

  const sorted = [...rows].sort(
    (a, b) => (b.deletedAt || 0) - (a.deletedAt || 0),
  );

  if (loading) {
    return (
      <div
        style={{ padding: 40, textAlign: "center", fontFamily: "monospace" }}
      >
        ⏳ ڈیٹا لوڈ ہو رہا ہے...
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-title">
        حذف شدہ ریکارڈ <span>Deleted History</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
        یہاں تمام صفحات سے حذف شدہ اندراجات 30 دن تک محفوظ رہتے ہیں، پھر خود
        بخود مستقل طور پر حذف ہو جاتے ہیں۔
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>صفحہ</th>
              <th>تفصیل</th>
              <th>تاریخ حذف</th>
              <th>باقی دن</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty">
                  کوئی حذف شدہ ریکارڈ موجود نہیں
                </td>
              </tr>
            ) : (
              sorted.map((row) => (
                <tr key={row.id}>
                  <td>{row.pageTitle}</td>
                  <td style={{ fontSize: 13 }}>{summarize(row)}</td>
                  <td className="td-num">
                    {row.deletedAt
                      ? new Date(row.deletedAt).toLocaleDateString("en-GB")
                      : "—"}
                  </td>
                  <td className="td-num">{daysLeft(row.deletedAt)} دن</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  // One ledger hook per page — fixed number of hooks, each tied to its own
  // Firestore collection (see PAGE_DEFS above).
  const charpaiSales = useLedger(PAGE_DEFS[0]);
  const binaiMazdoori = useLedger(PAGE_DEFS[1]);
  const weldingSaman = useLedger(PAGE_DEFS[2]);
  const sutar = useLedger(PAGE_DEFS[3]);
  const khataPaintOil = useLedger(PAGE_DEFS[4]);
  const drumNali = useLedger(PAGE_DEFS[5]);

  const ledgers = useMemo(
    () => [
      charpaiSales,
      binaiMazdoori,
      weldingSaman,
      sutar,
      khataPaintOil,
      drumNali,
    ],
    [charpaiSales, binaiMazdoori, weldingSaman, sutar, khataPaintOil, drumNali],
  );

  const dataLoading = ledgers.some((l) => l.loading);

  const deletedHistory = useDeletedHistory();

  // ── Totals for Dashboard + Report ──────────────────────────────────────
  const totals = useMemo(() => {
    return PAGE_DEFS.map((def, i) => {
      const rows = ledgers[i].rows;
      const hasAmount = def.fields.some((f) => f.key === "price");
      const amount = hasAmount
        ? rows.reduce((s, r) => s + (rowAmount(def, r) || 0), 0)
        : 0;
      return { def, count: rows.length, amount, hasAmount };
    });
  }, [ledgers]);

  const totalIncome = totals
    .filter((t) => t.def.kind === "income")
    .reduce((s, t) => s + t.amount, 0);
  const totalExpense = totals
    .filter((t) => t.def.kind === "expense")
    .reduce((s, t) => s + t.amount, 0);
  const netProfit = totalIncome - totalExpense;

  const barColors = [
    "#c94a1e",
    "#1a5276",
    "#2e7d4f",
    "#b8860b",
    "#7b1fa2",
    "#6b4226",
  ];
  const maxAmount = Math.max(...totals.map((t) => t.amount), 1);

  if (dataLoading) {
    return (
      <div
        style={{ padding: 40, textAlign: "center", fontFamily: "monospace" }}
      >
        ⏳ ڈیٹا لوڈ ہو رہا ہے...
      </div>
    );
  }

  return (
    <>
      {toast && <div className="toast">{toast}</div>}

      <header className="header">
        <div className="header-brand">
          <div className="header-icon">🏭</div>
          <div>
            <div className="header-title">ESPAK شاپ مینیجر</div>
            <div className="header-sub">Environmental Services Pakistan</div>
          </div>
        </div>
        <div className="header-right">
          <div className="header-user">
            <span className="user-badge">{user.role}</span>
            <span style={{ color: "#fffdf8", fontWeight: 700 }}>
              {user.username}
            </span>
          </div>
          <button className="logout-btn" onClick={onLogout}>
            🚪 لاگ آؤٹ
          </button>
        </div>
      </header>

      <nav className="nav">
        {TABS.map((t, i) => (
          <button
            key={i}
            className={`nav-tab${tab === i ? " active" : ""}`}
            onClick={() => setTab(i)}
          >
            {t}
          </button>
        ))}
      </nav>

      <main className="main">
        {/* DASHBOARD */}
        {tab === 0 && (
          <>
            <div className="kpi-grid">
              <div className="kpi" style={{ "--kpi-color": "var(--green)" }}>
                <div className="kpi-label">کل آمدنی (فروخت)</div>
                <div className="kpi-value">{fmt(totalIncome)}</div>
                <div className="kpi-sub">Total Income</div>
              </div>
              <div className="kpi" style={{ "--kpi-color": "var(--accent)" }}>
                <div className="kpi-label">کل اخراجات</div>
                <div className="kpi-value">{fmt(totalExpense)}</div>
                <div className="kpi-sub">Total Expenses</div>
              </div>
              <div
                className="kpi"
                style={{
                  "--kpi-color":
                    netProfit >= 0 ? "var(--green)" : "var(--accent)",
                }}
              >
                <div className="kpi-label">خالص منافع</div>
                <div className="kpi-value">{fmt(Math.abs(netProfit))}</div>
                <div className="kpi-sub">
                  {netProfit >= 0 ? "✅ فائدہ" : "⚠️ نقصان"}
                </div>
              </div>
              {totals.map((t, i) => (
                <div
                  className="kpi"
                  key={t.def.key}
                  style={{ "--kpi-color": t.def.color }}
                >
                  <div className="kpi-label">{t.def.title}</div>
                  <div className="kpi-value">{t.count}</div>
                  <div className="kpi-sub">
                    {t.hasAmount ? fmt(t.amount) : "اندراجات"}
                  </div>
                </div>
              ))}
            </div>

            <div className="section">
              <div className="section-title">
                زمرے کے مطابق رقم <span>Bar Chart</span>
              </div>
              <div className="form-card">
                <div className="bar-chart">
                  {totals.map((t, i) => (
                    <div className="bar-row" key={t.def.key}>
                      <div className="bar-label">{t.def.title}</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{
                            width:
                              Math.round((t.amount / maxAmount) * 100) + "%",
                            background: barColors[i % barColors.length],
                          }}
                        >
                          <span>{t.hasAmount ? fmt(t.amount) : t.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* PAGE 2-7: one LedgerPage per PAGE_DEFS entry */}
        {tab >= 1 && tab <= 6 && (
          <LedgerPage
            def={PAGE_DEFS[tab - 1]}
            ledger={ledgers[tab - 1]}
            showToast={showToast}
          />
        )}

        {/* REPORT */}
        {tab === 7 && (
          <>
            <div className="section">
              <div className="section-title">
                مالی رپورٹ <span>Financial Report</span>
              </div>
              <div className="report-block">
                <div className="report-row">
                  <span>کل آمدنی — چارپائی فروخت</span>
                  <span className="td-num" style={{ color: "var(--green)" }}>
                    {fmt(totalIncome)}
                  </span>
                </div>
                {totals
                  .filter((t) => t.def.kind === "expense")
                  .map((t) => (
                    <div className="report-row" key={t.def.key}>
                      <span>{t.def.title}</span>
                      <span
                        className="td-num"
                        style={{ color: "var(--accent)" }}
                      >
                        − {fmt(t.amount)}
                      </span>
                    </div>
                  ))}
                <div
                  className={`report-row total ${netProfit >= 0 ? "profit" : "loss"}`}
                >
                  <span>
                    {netProfit >= 0 ? "✅ خالص منافع" : "⚠️ خالص نقصان"}
                  </span>
                  <span className="td-num">{fmt(Math.abs(netProfit))}</span>
                </div>
              </div>
            </div>

            <div className="section">
              <div className="section-title">
                صفحات کا خلاصہ <span>Pages Summary</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>صفحہ</th>
                      <th>قسم</th>
                      <th>کل اندراجات</th>
                      <th>کل رقم</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totals.map((t) => (
                      <tr key={t.def.key}>
                        <td>{t.def.title}</td>
                        <td>
                          {t.def.kind === "income" && (
                            <span className="pill pill-income">آمدنی</span>
                          )}
                          {t.def.kind === "expense" && (
                            <span className="pill pill-expense">خرچہ</span>
                          )}
                          {t.def.kind === "ledger" && (
                            <span
                              className="pill"
                              style={{
                                background: "#eee",
                                color: "var(--muted)",
                              }}
                            >
                              کھاتہ
                            </span>
                          )}
                        </td>
                        <td className="td-num">{t.count}</td>
                        <td className="td-num">
                          {t.hasAmount ? fmt(t.amount) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* DELETED HISTORY */}
        {tab === 8 && (
          <DeletedHistoryPage
            rows={deletedHistory.rows}
            loading={deletedHistory.loading}
          />
        )}
      </main>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ShopDashboard() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Firebase keeps the session itself (survives refresh/closing the tab) —
  // this just listens for it and figures out the username + role.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const username = fbUser.email.split("@")[0];
        setLoggedInUser({
          username,
          role: ROLE_MAP[username] || "منیجر",
        });
      } else {
        setLoggedInUser(null);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const handleLogout = () => signOut(auth);

  if (authLoading) {
    return (
      <>
        <style>{css}</style>
        <div className="app" />
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="app">
        {loggedInUser ? (
          <Dashboard user={loggedInUser} onLogout={handleLogout} />
        ) : (
          <LoginPage onLogin={() => {}} />
        )}
      </div>
    </>
  );
}
